import React, { useEffect, useRef, useState } from 'react'
import api from 'helpers/api'
import { Dashboard } from 'components/icons'
import ChatPanel from 'components/chat'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { ICustomViewVersion } from 'types'
import CustomViewFrame from './frame'
import Callout from './callout'
import VersionsPanel, { VersionBanner } from './versions-panel'
import PublishBanner from './publish-banner'
import { getUnpublishedVersion } from 'components/spreadsheet/helpers/customViewLatest'
import PageSkeleton from './skeleton'
import { viewIdeas } from './ideas'
import CustomViewPrompt, { CustomViewBuilding } from './prompt'
import { rowSettings } from './access'
import { useSyncStartedThisSession } from 'components/spreadsheet/helpers/syncSession'
import { visibleViewColumns } from './source'
import {
  PANEL_MAX_WIDTH,
  PANEL_MIN_WIDTH,
  CustomViewPanelMode,
  clampPanelWidth,
  closeCustomViewPanel,
  openCustomViewPanel,
  setCustomViewEditing,
  readPanelWidth,
  useCustomViewPanel,
  writePanelWidth
} from './panel'
import { STARTER_PAGE } from './runtime'

function EmptyState() {
  return (
    <div className="flex flex-1 items-center justify-center p-8">
      <div className="flex flex-col items-center max-w-sm text-center">
        <span className="flex items-center justify-center w-14 h-14 mb-4 rounded-2xl bg-really-light-blue text-blue">
          <Dashboard className="w-6 h-6" />
        </span>
        <h2 className="text-lg font-semibold text-primary">Nothing here yet</h2>
        <p className="mt-1 text-sm text-secondary">
          A table owner can build a dashboard or report for this view. It will appear here once it has been saved.
        </p>
      </div>
    </div>
  )
}

/** Drag (or arrow keys on) the panel's left edge to size it; the width is kept per browser. */
function ResizeEdge({ width, onResize }: { width: number; onResize: (width: number, done?: boolean) => void }) {
  const start = (event: React.PointerEvent) => {
    event.preventDefault()
    const startX = event.clientX
    const startWidth = width
    const previousUserSelect = document.body.style.userSelect
    document.body.style.userSelect = 'none'
    // The page's frame would swallow pointer moves that cross it.
    document.body.classList.add('[&_iframe]:pointer-events-none')
    let latest = startWidth
    const move = (moveEvent: PointerEvent) => {
      latest = clampPanelWidth(startWidth + startX - moveEvent.clientX)
      onResize(latest)
    }
    const end = () => {
      document.body.style.userSelect = previousUserSelect
      document.body.classList.remove('[&_iframe]:pointer-events-none')
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', end)
      window.removeEventListener('pointercancel', end)
      onResize(latest, true)
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', end)
    window.addEventListener('pointercancel', end)
  }

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize panel"
      aria-valuenow={width}
      aria-valuemin={PANEL_MIN_WIDTH}
      aria-valuemax={PANEL_MAX_WIDTH}
      // A focusable separator is the ARIA window-splitter pattern; the rule doesn't know a separator can be interactive.
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      onPointerDown={start}
      onKeyDown={(event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
        event.preventDefault()
        onResize(clampPanelWidth(width + (event.key === 'ArrowLeft' ? 24 : -24)), true)
      }}
      className="absolute inset-y-0 -left-1 z-10 w-2 cursor-col-resize outline-none group"
    >
      <span className="absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 bg-transparent transition-colors group-hover:bg-blue group-focus-visible:bg-blue" />
    </div>
  )
}

/**
 * Owner editor. The page fills the view and a panel on the right holds MortaAI or the page's versions. Every page
 * MortaAI saves adds a version; owners see the newest, which the bar over the page publishes or discards. Versions
 * previews any of them in the page, read-only, under a bar that publishes it.
 */
const PANEL_LABELS: Record<CustomViewPanelMode, string> = { ai: 'MortaAI', versions: 'Versions' }

function PageEditor({ savedHtml }: { savedHtml?: string }) {
  const { spreadsheetData } = useDataContext()
  const syncStartedThisSession = useSyncStartedThisSession(spreadsheetData.tableDetails.publicId)
  const panel = useCustomViewPanel()
  const [width, setWidth] = useState(() => clampPanelWidth(readPanelWidth()))
  const viewId = spreadsheetData.viewDetails.publicId
  const html = savedHtml ?? STARTER_PAGE
  // An older version picked in Versions, shown in the page in place of the newest.
  const [viewing, setViewing] = useState<ICustomViewVersion | null>(null)
  const shown = viewing?.html ?? html
  const shownVersionId =
    viewing?.id ?? spreadsheetData.viewDetails.customViewLatest?.id ?? spreadsheetData.viewDetails.customView?.version
  // The published version, previewed, is the live page everyone uses: it works as it does for them, with no bar over
  // it (there's nothing to publish, and the newest is a click away in Versions).
  const viewingPublished = viewing !== null && viewing.id === spreadsheetData.viewDetails.customView?.version
  // Any other older version may not change rows: only the newest version (or the published page) does.
  const readOnly = viewing !== null && !viewingPublished
  // MortaAI's newest version while it isn't published: the banner over the page publishes or discards it.
  const unpublished = getUnpublishedVersion(spreadsheetData)
  // A view never saved opens on the prompt; the panel opens once it's asked.
  const [showPrompt, setShowPrompt] = useState(savedHtml === undefined)
  const prompting = showPrompt && html === STARTER_PAGE
  const [firstPrompt, setFirstPrompt] = useState<string>()
  const [firstFiles, setFirstFiles] = useState<File[]>()
  // The first prompt goes to the panel once. After that the panel, closed and reopened, carries on the
  // view's conversation like any other time, rather than sending the prompt again in a new one.
  const [promptSent, setPromptSent] = useState(false)
  // The pencil (or the prompt, on a new view) is MortaAI's way in here, so the floating MortaAI steps aside.
  useEffect(() => {
    setCustomViewEditing(true)
    return () => setCustomViewEditing(false)
  }, [])
  const { tableDetails } = spreadsheetData
  // The view's row settings as its users get them, from the helper the page's own access uses (a sync running or
  // started this session closes the table to new rows too). Owners can always delete, as in the grid, so only a
  // synced table turns deleting off for everyone.
  const offActions = [
    shown.includes('morta.addRows') && !rowSettings(spreadsheetData, syncStartedThisSession).add && 'add rows',
    shown.includes('morta.deleteRows') && tableDetails.isSynced && 'delete rows'
  ].filter(Boolean)

  // A view that loses its only page (its newest version gone) has no page again, so it goes back to its prompt.
  const hadPage = useRef(savedHtml !== undefined)
  useEffect(() => {
    if (savedHtml === undefined && hadPage.current) {
      setFirstPrompt(undefined)
      setFirstFiles(undefined)
      // The next prompt starts the page again, so it goes to MortaAI like the first one did.
      setPromptSent(false)
      setShowPrompt(true)
    }
    hadPage.current = savedHtml !== undefined
  }, [savedHtml])

  // The panel closes with the view.
  useEffect(() => closeCustomViewPanel, [viewId])

  const ask = (prompt: string, files?: File[]) => {
    setFirstFiles(files)
    setFirstPrompt(prompt)
    setShowPrompt(false)
    openCustomViewPanel('ai')
  }

  const resize = (next: number, done?: boolean) => {
    setWidth(next)
    if (done) writePanelWidth(next)
  }

  // Until MortaAI's first page arrives, the view shows it being built rather than the starter page.
  const building = firstPrompt !== undefined && panel.working && html === STARTER_PAGE

  const renderPage = () => {
    if (prompting) {
      return (
        <CustomViewPrompt
          tableName={tableDetails.name}
          ideas={viewIdeas(visibleViewColumns(spreadsheetData), tableDetails.name)}
          onSubmit={ask}
          names={[tableDetails.name, ...visibleViewColumns(spreadsheetData).map((column) => column.name)]}
        />
      )
    }
    if (building) {
      return <CustomViewBuilding />
    }
    return null
  }
  const page = renderPage()

  return (
    <section className="flex h-full min-h-0 bg-surface" data-testid="custom-view-view">
      <div className="flex flex-col flex-1 min-w-0 min-h-0">
        {page}
        {!page && !viewing && unpublished && <PublishBanner version={unpublished} />}
        {!page && offActions.length > 0 && (
          <div className="p-3 border-b border-divider">
            <Callout tone="warning">
              This page tries to {offActions.join(' and ')}, which this view&apos;s settings turn off for the people
              using it, so those controls won&apos;t work. Turn on Allow Add New Row or Allow Row Deletion in the
              view&apos;s menu, or remove the controls.
            </Callout>
          </div>
        )}
        {!page && viewing && !viewingPublished && <VersionBanner version={viewing} onExit={() => setViewing(null)} />}
        {!page && (
          // Which version is shown, so Versions can take a picture of it (thumbnail.ts).
          <div className="flex flex-col flex-1 min-h-0" data-custom-view-version={shownVersionId}>
            <CustomViewFrame key={shown} html={shown} readOnly={readOnly} />
          </div>
        )}
      </div>

      {panel.open && (
        <aside
          className="relative flex flex-col shrink-0 min-h-0 border-l border-divider bg-surface"
          // Re-clamped each render, so a window made narrower never leaves the panel too wide.
          style={{ width: clampPanelWidth(width) }}
          aria-label={PANEL_LABELS[panel.mode]}
          data-testid="custom-view-panel"
        >
          <ResizeEdge width={width} onResize={resize} />
          {panel.mode === 'ai' && (
            <div className="flex flex-col flex-1 min-h-0">
              <ChatPanel
                docked
                isOpen
                onClose={closeCustomViewPanel}
                onStartTour={() => undefined}
                initialPrompt={promptSent ? undefined : firstPrompt}
                initialFiles={promptSent ? undefined : firstFiles}
                onInitialPromptSent={() => setPromptSent(true)}
              />
            </div>
          )}
          {panel.mode === 'versions' && <VersionsPanel previewing={viewing} onPreview={setViewing} />}
        </aside>
      )}
    </section>
  )
}

function useSavedHtml(viewId: string, page?: { updatedAt?: string | null; html?: string | null } | null) {
  const [fetched, setFetched] = useState<{ version: string; html: string | null } | null>(null)
  const [failed, setFailed] = useState(false)
  // Any saved page without HTML in hand is fetched, even if its metadata lacks a timestamp.
  const version = page?.updatedAt ?? ''
  // Null is the server saying the page's HTML can't be found: nothing to fetch, and no page.
  const needsFetch = Boolean(page) && page?.html === undefined

  useEffect(() => {
    if (!needsFetch) return
    let cancelled = false
    setFailed(false)
    api
      .getTableView(viewId)
      .then((response) => {
        // The view response carries the HTML; views added from other responses may only have metadata.
        // A page whose HTML can't be found (null) is no page, never a blank one.
        if (!cancelled) setFetched({ version, html: response.data.customView?.html ?? null })
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [viewId, version, needsFetch])

  if (!page) return { html: undefined, loading: false, failed: false }
  if (page.html !== undefined) return { html: page.html ?? undefined, loading: false, failed: false }
  if (fetched?.version !== version) return { html: undefined, loading: !failed, failed }
  return { html: fetched.html ?? undefined, loading: false, failed }
}

/**
 * An owner's view loads its newest version, with the published page's HTML, before the editor shows: the list of
 * views carries neither. Readers never get the newest version, only the published one.
 */
function useOwnerPages(viewId: string, needed: boolean) {
  const { setSpreadsheetData } = useDataContext()
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!needed) return
    let cancelled = false
    setFailed(false)
    api
      .getTableView(viewId)
      .then((response) => {
        if (cancelled) return
        setSpreadsheetData({ type: 'SET_CUSTOM_VIEW', viewId, customView: response.data.customView ?? null })
        setSpreadsheetData({ type: 'SET_CUSTOM_VIEW_LATEST', viewId, latest: response.data.customViewLatest ?? null })
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [viewId, needed])

  return { loading: needed && !failed, failed }
}

export default function CustomView() {
  const { spreadsheetData } = useDataContext()
  const view = spreadsheetData.viewDetails
  const page = view.customView
  const canEdit = spreadsheetData.isAdmin && !view.isPersonalView
  const owner = useOwnerPages(view.publicId, canEdit && view.customViewLatest === undefined)
  // A page built for a newer runtime is never fetched or run; an owner's comes with their newest version.
  const saved = useSavedHtml(view.publicId, page?.runtimeVersion === 1 && !owner.loading ? page : null)

  const renderStatus = () => {
    if (page && page.runtimeVersion !== 1) {
      return (
        <div className="p-4">
          <Callout tone="warning">This view was built for a newer version of Morta. Reload to view it.</Callout>
        </div>
      )
    }
    if (saved.loading || owner.loading) {
      return <PageSkeleton />
    }
    if (saved.failed || owner.failed) {
      return (
        <div className="p-4">
          <Callout>This view could not be loaded. Refresh to try again.</Callout>
        </div>
      )
    }
    return null
  }

  const status = renderStatus()
  // Keyed by the view, so nothing the editor holds (its prompt, MortaAI's first message) carries over to another view.
  // An owner works on the newest version when it isn't the published one, else the published page.
  if (!status && canEdit) {
    return <PageEditor key={view.publicId} savedHtml={view.customViewLatest?.html ?? saved.html} />
  }

  const renderViewer = () => {
    if (saved.html === undefined) return <EmptyState />
    // The frame reads the table view's data context itself and follows (or reloads on) filters, sorts and hidden
    // columns that re-shape what it reads, so it is only keyed by the view and its page.
    return <CustomViewFrame key={`${view.publicId}:${saved.html}`} html={saved.html} />
  }

  return (
    <section className="flex flex-col h-full bg-surface" data-testid="custom-view-view">
      {status ?? renderViewer()}
    </section>
  )
}
